/**
 * PGSS-5748 part 3: Scan to load. Pure helpers for the screen, so its rules are tested without rendering it:
 * how each scan result is shown, the offline queue, and the box lists.
 *
 * The server decides everything about a scan (GET/POST /api/shipments/{id}/boxes[/scan]); this module only shows
 * what it answered. Only LOADED records anything. Nothing here blocks the existing container flow: unscanned boxes
 * are a warning on this screen, never a refusal elsewhere.
 */
import { isNetworkConnectivityError, isTransientOutageError } from 'app/utils/apiError';
import { formatBoxCode } from 'app/main/apps/e-commerce/order/tabs/boxLabel';

/** Results the server answers, plus two of the screen's own: QUEUED (saved offline) and ERROR (the request failed). */
export const SCAN_RESULTS = {
  LOADED: { tone: 'success', label: 'Loaded' },
  ALREADY_LOADED: { tone: 'neutral', label: 'Already loaded' },
  WRONG_CONTAINER: { tone: 'error', label: 'Wrong container' },
  OUT_OF_DATE: { tone: 'warning', label: 'Label out of date' },
  UNKNOWN_CODE: { tone: 'error', label: 'Unknown code' },
  NOT_A_BOX_CODE: { tone: 'warning', label: 'Not a box code' },
  CONTAINER_CLOSED: { tone: 'warning', label: 'Container closed' },
  QUEUED: { tone: 'info', label: 'Saved, waiting to send' },
  ERROR: { tone: 'error', label: 'Not sent' },
};

/** Banner colours per tone: background and text, readable on a phone in daylight. */
export const TONE_COLORS = {
  success: { background: '#2e7d32', color: '#ffffff' },
  neutral: { background: '#eceff1', color: '#263238' },
  info: { background: '#e3f2fd', color: '#0d47a1' },
  warning: { background: '#ed6c02', color: '#ffffff' },
  error: { background: '#c62828', color: '#ffffff' },
};

/** How a result is shown: its tone, label and colours. An unknown result reads as an error, never as a success. */
export function scanResultView(result) {
  const known = SCAN_RESULTS[result] || SCAN_RESULTS.ERROR;
  return { ...known, ...TONE_COLORS[known.tone] };
}

/** True when the scan recorded the box. Only LOADED does. */
export function isRecorded(result) {
  return result === 'LOADED';
}

/** What was scanned or typed, trimmed. The server reads dashes, spaces, case, O/0 and I/L/1 itself. */
export function cleanScannedCode(raw) {
  return typeof raw === 'string' ? raw.trim() : '';
}

/** A box code as printed (XXXX-XXXX-XX); anything else is shown as given. */
export function displayCode(code) {
  return formatBoxCode(code);
}

/** "12 / 30". */
export function countsText(loaded, total) {
  return `${Number(loaded) || 0} / ${Number(total) || 0}`;
}

/** How many boxes are not scanned yet (never negative). */
export function notScannedCount(loaded, total) {
  return Math.max(0, (Number(total) || 0) - (Number(loaded) || 0));
}

/** "1 box" / "3 boxes". */
export function boxesText(count) {
  return `${count} ${count === 1 ? 'box' : 'boxes'}`;
}

/** The container's boxes, split into the two lists the screen shows, each in label order as the server sent them. */
export function splitBoxes(boxes) {
  const list = Array.isArray(boxes) ? boxes : [];
  return {
    toLoad: list.filter((box) => !box?.loaded),
    loaded: list.filter((box) => box?.loaded),
  };
}

/**
 * The list after a scan, before the refetch lands: a LOADED box moves to Loaded at once, and the counts follow the
 * scan's answer. Any other result leaves the boxes as they were.
 */
export function applyScan(list, scan) {
  if (!list || !scan) return list;
  const counts = {
    loaded: Number.isFinite(scan.loaded) ? scan.loaded : list.loaded,
    total: Number.isFinite(scan.total) ? scan.total : list.total,
  };
  if (!isRecorded(scan.result) || !scan.box?.code) return { ...list, ...counts };
  const boxes = (list.boxes || []).map((box) => (box.code === scan.box.code
    ? { ...box, loaded: true, loadedAt: scan.box.loadedAt ?? box.loadedAt }
    : box));
  return { ...list, ...counts, boxes };
}

/**
 * A loadedAt from the server as a local date and time ("Nov 4, 2026, 02:15 PM"); the server may send an ISO string or
 * epoch seconds. PGSS-5887: the date as well, since a list holds boxes loaded on different days.
 */
export function loadedAtText(value) {
  if (value === null || value === undefined || value === '') return '';
  const date = typeof value === 'number' ? new Date(value < 1e12 ? value * 1000 : value) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* ---------------------------------------------------------------- offline queue */

/**
 * How long a scan or the box list may take before it counts as no connection. Weak signal keeps `navigator.onLine`
 * true while a request hangs; without a limit every later scan would wait behind it.
 */
export const REQUEST_TIMEOUT_MS = 8000;

/** True when a scan failed because the server could not be reached, so it is kept and sent later. */
export function isOfflineError(error) {
  return isNetworkConnectivityError(error) || isTransientOutageError(error);
}

/**
 * One queue per container and per user: a phone used on two containers never sends a scan to the wrong one, and on a
 * shared phone one user's waiting scans are never sent (and recorded) under another user's login.
 */
export function queueKey(shipmentId, userId) {
  return `psg.scanToLoad.queue.${userId || 'anonymous'}.${shipmentId}`;
}

/** Who is scanning, for the queue key: the same id the app's other per-user settings use. */
export function scanUserId(user) {
  return user?.uuid || user?.id || user?.data?.email || '';
}

/** The waiting scans for a container. Storage may be missing or throw (private mode); then the queue is empty. */
export function readQueue(storage, shipmentId, userId) {
  try {
    const raw = storage?.getItem(queueKey(shipmentId, userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item.code === 'string' && item.code) : [];
  } catch {
    return [];
  }
}

/** Saves the waiting scans; returns false when storage refused, so the screen can say they live only in this tab. */
export function writeQueue(storage, shipmentId, userId, queue) {
  try {
    if (!storage) return false;
    if (!queue || queue.length === 0) storage.removeItem(queueKey(shipmentId, userId));
    else storage.setItem(queueKey(shipmentId, userId), JSON.stringify(queue));
    return true;
  } catch {
    return false;
  }
}

/** Adds a scan to the end of the queue. A code already waiting is not added twice (the server would count it once). */
export function enqueue(queue, code, now = Date.now()) {
  const list = Array.isArray(queue) ? queue : [];
  if (!code || list.some((item) => item.code === code)) return list;
  return [...list, { code, at: now }];
}

/** Removes one waiting scan (the user discards it). */
export function removeFromQueue(queue, code) {
  return (Array.isArray(queue) ? queue : []).filter((item) => item.code !== code);
}

/**
 * Sends the waiting scans in order. A scan the server answered (any result, a refusal included) leaves the queue
 * and its answer is reported. A network failure stops the run and keeps it and everything after it. Any other
 * failure (403, 500, ...) keeps the scan with the server's message, so it is never dropped silently, and the run
 * moves on to the next one.
 *
 * `send(code)` resolves to the scan response or rejects with the request error. `stillQueued(item)`, when given, is
 * asked before each send, so a scan removed while the run is going is not sent. Returns the queue left and the
 * answers, in order: `{ code, scan }` for an answered scan, `{ code, error }` for a kept one.
 */
export async function drainQueue(queue, send, stillQueued) {
  const pending = Array.isArray(queue) ? [...queue] : [];
  const left = [];
  const answers = [];
  for (let i = 0; i < pending.length; i += 1) {
    const item = pending[i];
    // eslint-disable-next-line no-continue
    if (stillQueued && !stillQueued(item)) continue;
    try {
      // Sequential on purpose: the queue is sent in the order it was scanned.
      // eslint-disable-next-line no-await-in-loop
      const scan = await send(item.code);
      answers.push({ code: item.code, scan });
    } catch (error) {
      if (isOfflineError(error)) {
        return { left: [...left, ...pending.slice(i)], answers, offline: true };
      }
      const message = error?.message || 'This scan could not be sent.';
      left.push({ ...item, error: message });
      answers.push({ code: item.code, error: message });
    }
  }
  return { left, answers, offline: false };
}

/**
 * The queue after a send run, applied to the queue as it is NOW (not the copy the run started from): answered scans
 * leave, scans kept with an error get that error, and anything removed or added meanwhile stays removed or added.
 */
export function settleQueue(current, answers) {
  const list = Array.isArray(current) ? current : [];
  const answered = new Set((answers || []).filter((a) => a.scan).map((a) => a.code));
  const errors = new Map((answers || []).filter((a) => a.error).map((a) => [a.code, a.error]));
  return list
    .filter((item) => !answered.has(item.code))
    .map((item) => (errors.has(item.code) ? { ...item, error: errors.get(item.code) } : item));
}

/* ---------------------------------------------------------------- camera */

/**
 * The camera reads the same QR many times a second. A code is taken once, and the same code again only after a
 * different one was read or `windowMs` passed, so a box held in front of the camera is one scan and its green
 * "Loaded" banner is not replaced by "Already loaded" a moment later.
 */
export const CAMERA_REPEAT_MS = 10000;

export function shouldAcceptCameraCode(last, code, now, windowMs = CAMERA_REPEAT_MS) {
  if (!code) return false;
  if (!last || last.code !== code) return true;
  return now - last.at >= windowMs;
}

/** Why a container's boxes could not be opened, in words for the user. */
export function boxListErrorText(error) {
  const status = error?.response?.status ?? error?.status;
  if (status === 404) return "This shipment isn't a supplier container, so it has no boxes to scan.";
  if (status === 403) return 'This container belongs to another supplier.';
  if (isOfflineError(error)) return "Can't reach the server. Scans you make are saved on this device and sent when you're back online.";
  return error?.message || 'The boxes could not be loaded.';
}

/**
 * True when a shipping-label response (GET /api/shipmentLabel) carries box codes: a supplier container whose labels
 * are printed, so its boxes can be scanned. Other shipments never get box codes, so they are not offered Scan to load.
 */
export function labelsHaveBoxCodes(shippingLabel) {
  const labels = shippingLabel?.shipping_label?.orders;
  return Array.isArray(labels) && labels.some((label) => !!label?.box_code);
}

/**
 * PGSS-5875: Scan to load is offered on the label page only while a scan can be recorded: the labels carry box codes
 * and the container hasn't shipped. Once shipped, invoiced or paid, the scan endpoint refuses every box.
 */
export function canOfferScanToLoad(shippingLabel) {
  return labelsHaveBoxCodes(shippingLabel) && shippingLabel?.shipping_label?.closed_for_loading !== true;
}
